import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  CanvasActionItemExecutionEntity,
  Entity,
} from "@cohesive/api-entities";
import { capitalCase } from "change-case";
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { FileRecordThumbnail } from "../../FileRecordPreview";
import { ActionItemCard, ActionItemCardAvatar } from "../ActionItemCard";
import {
  ActionItemExecutionCard,
  ActionItemExecutionCardAvatar,
} from "../ActionItemExecutionCard";
import { ContextMenuAction, Process } from "../BaseCard";
import { CalendarCard, CalendarCardAvatar } from "../CalendarCard/CalendarCard";
import {
  CalendarEventCard,
  CalendarEventCardAvatar,
} from "../CalendarEventCard";
import { CallCard, CallCardAvatar } from "../CallCard";
import { ChannelCard, ChannelCardAvatar } from "../ChannelCard";
import { CommentCard, CommentCardAvatar } from "../CommentCard";
import { CompanyCard, CompanyCardAvatar } from "../CompanyCard";
import { ContactCard, ContactCardAvatar } from "../ContactCard";
import { DatabaseCard, DatabaseCardAvatar } from "../DatabaseCard";
import { DealCard, DealCardAvatar } from "../DealCard";
import { DocumentCard, DocumentCardAvatar } from "../DocumentCard";
import { EmailCard, EmailCardAvatar } from "../EmailCard";
import { EmailFolderCard, EmailFolderCardAvatar } from "../EmailFolderCard";
import { EmailThreadCard, EmailThreadCardAvatar } from "../EmailThreadCard";
import { FileFolderCard, FileFolderCardAvatar } from "../FileFolderCard";
import { FileRecordCard } from "../FileRecordCard/FileRecordCard";
import { GenericCard, GenericCardAvatar } from "../GenericCard";
import { GroupCard, GroupCardAvatar } from "../GroupCard";
import { IssueCard, IssueCardAvatar } from "../IssueCard";
import { IssueTypeCard, IssueTypeCardAvatar } from "../IssueTypeCard";
import {
  IterationCard,
  IterationCardAvatar,
} from "../IterationCard/IterationCard";
import { LabelCard, LabelCardAvatar } from "../LabelCard/LabelCard";
import { MeetingCard, MeetingCardAvatar } from "../MeetingCard";
import { MessageCard, MessageCardAvatar } from "../MessageCard";
import { NoteCard, NoteCardAvatar } from "../NoteCard";
import { ProjectCard, ProjectCardAvatar } from "../ProjectCard";
import {
  ProjectItemCard,
  ProjectItemCardAvatar,
} from "../ProjectItemCard/ProjectItemCard";
import {
  PullRequestCard,
  PullRequestCardAvatar,
} from "../PullRequestCard/PullRequestCard";
import {
  RepositoryCard,
  RepositoryCardAvatar,
} from "../RepositoryCard/RepositoryCard";
import { SkillCardEntity } from "../SkillCard";
import { SpreadsheetCard, SpreadsheetCardAvatar } from "../SpreadsheetCard";
import { TicketCard, TicketCardAvatar } from "../TicketCard";
import { ToolCard, ToolCardAvatar } from "../ToolCard";
import { legacyArtifactEntityDataToFileRecord } from "./legacyArtifactEntityAdapter";
import { legacyFileEntityDataToFileRecord } from "./legacyFileEntityAdapter";

export type { Entity };

export type EntityCardProps = {
  entity: Entity;
  onClick?: (e?: MouseEvent) => void;
  onMentionClick?: () => void;
  onDoubleClick?: () => void;
  onQuickChatSubmit?: (message: string) => void;
  onDeleteClick?: () => void;
  onDetailClick?: () => void;
  toolbarMode?: "hover" | "external";
  chatOpen?: boolean;
  onChatOpenChange?: (open: boolean) => void;
  actions?: ContextMenuAction[];
  processes?: Process[];
  /** Canvas / grid selection ring for `file_record` cards */
  selected?: boolean;
  /** Workspace file actions dropdown */
  menuSlot?: ReactNode;
  /** When false, `FileRecordCard` hides the filename/footer row. */
  showFooter?: boolean;
  /**
   * When true, `FileRecordCard` drops its own chrome (border / shadow /
   * rounded-2xl) and fills its parent container's height. Used by canvas
   * spatial nodes where the React Flow node already provides chrome.
   * Independent of `showFooter`.
   */
  embedded?: boolean;
  /** `FileRecordCard` layout variant — `"compact"` for chat attachments. */
  variant?: "default" | "compact";
  className?: string;
  style?: CSSProperties;
  /** Forwarded to `FileRecordCard.thumbnailWrapper` (and ignored
   *  for non-file entities) so callers can scope hover affordances
   *  to the thumbnail area only. */
  thumbnailWrapper?: (thumb: ReactNode) => ReactNode;
  /** Canvas video upload handoff — revoke local blob after server video paints. */
  onVideoDisplayReady?: () => void;
  /** Reference appearance — see FileRecordCard.isReference. */
  isReference?: boolean;
};

function isArtifactEntity(entity: Entity): boolean {
  return "type" in entity && (entity as any).type === "artifact";
}

/**
 * Legacy `canvas_file` (rag_files-backed) uploads. Their backing storage
 * has been retired client-side along with the orgUserGetRagFileHandler
 * callers; rendering anywhere is intentionally a no-op until the backend
 * cleanup pass deletes the rows. External `base: "file"` integrations
 * (Google Drive, Dropbox, Slack file shares, …) keep rendering through
 * the normal file branches below.
 */
function isLegacyCanvasFileEntity(entity: Entity): boolean {
  if (!("base" in entity) || entity.base !== "file") return false;
  const e = entity as { platform?: string; type?: string };
  return e.platform === "cohesive" || e.type === "canvas_file";
}

function isActionItemExecutionEntity(
  entity: Entity,
): entity is CanvasActionItemExecutionEntity {
  return "type" in entity && entity.type === "canvas_action_item_execution";
}

export const EntityCard = ({ entity, ...rest }: EntityCardProps) => {
  // Legacy canvas_file (rag_files-backed) uploads no longer render anywhere
  // in the client. Returning null means the host layout collapses around
  // them — chat bubbles drop the attachment block, list rows shrink, etc.
  // Backend cleanup (deleting the rows) ships in a later pass.
  if (isLegacyCanvasFileEntity(entity)) {
    return null;
  }

  // Legacy artifact entities (still embedded in canvas Loro state and old
  // chat messages) are adapted to a synthetic file_record so they render via
  // the unified FileRecordCard. The synthesized id matches the real
  // post-migration file_record, so click handlers can fetch full data on
  // demand via `useOpenFileRecord`.
  if (isArtifactEntity(entity)) {
    const { selected, menuSlot, showFooter, embedded, ...artifactRest } = rest;
    const record = legacyArtifactEntityDataToFileRecord(
      entity.data as unknown as Record<string, unknown>,
    );
    return (
      <FileRecordCard
        platform={entity.platform}
        {...record}
        {...artifactRest}
        isSelected={!!selected}
        menuSlot={menuSlot}
        showFooter={showFooter}
        embedded={embedded}
        mediaIntent={embedded ? "full" : "thumbnail"}
      />
    );
  }

  // Handle canvas action item execution by type
  if (isActionItemExecutionEntity(entity)) {
    return <ActionItemExecutionCard {...entity.data} {...rest} />;
  }

  // Handle skill entities by type
  if ("type" in entity && (entity as any).type === "skill") {
    const skillData = (entity as any).data;
    return <SkillCardEntity {...skillData} {...rest} />;
  }

  if ("base" in entity) {
    switch (entity.base) {
      case "generic":
        return <GenericCard {...entity.data} {...rest} />;
      case "action_item":
        return (
          <ActionItemCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "contact":
        return (
          <ContactCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "email":
        return (
          <EmailCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "email_thread":
        return (
          <EmailThreadCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "email_folder":
        return (
          <EmailFolderCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "deal":
        return (
          <DealCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "comment":
        return (
          <CommentCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "company":
        return (
          <CompanyCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "database":
        return (
          <DatabaseCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "note":
        return (
          <NoteCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "tool":
        return <ToolCard {...entity.data} {...rest} />;
      case "calendar":
        return (
          <CalendarCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "calendar_event":
        return (
          <CalendarEventCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "document":
        return (
          <DocumentCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
            actions={rest.actions}
          />
        );
      case "spreadsheet":
        return (
          <SpreadsheetCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "project":
        return (
          <ProjectCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "repo":
        return (
          <RepositoryCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "issue":
        return (
          <IssueCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "issue_type":
        return (
          <IssueTypeCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "label":
        return (
          <LabelCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "iteration":
        return (
          <IterationCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "item":
        return (
          <ProjectItemCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "pull_request":
        return (
          <PullRequestCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "channel":
        return (
          <ChannelCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "message":
        return (
          <MessageCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "meeting":
        return (
          <MeetingCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "file":
        return (
          <FileFolderCard
            platform={entity.platform}
            {...(entity.data as any)}
            {...rest}
          />
        );
      case "video": {
        const { selected, menuSlot, showFooter, embedded, ...videoRest } = rest;
        const record = legacyFileEntityDataToFileRecord(
          "video",
          entity.data as unknown as Record<string, unknown>,
        );
        return (
          <FileRecordCard
            platform={entity.platform}
            {...record}
            {...videoRest}
            isSelected={!!selected}
            menuSlot={menuSlot}
            showFooter={showFooter}
            embedded={embedded}
            mediaIntent={embedded ? "full" : "thumbnail"}
          />
        );
      }
      case "file_folder":
        return (
          <FileFolderCard
            platform={entity.platform}
            {...entity.data}
            {...rest}
          />
        );
      case "group":
        return (
          <GroupCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "image": {
        const { selected, menuSlot, showFooter, embedded, ...imageRest } = rest;
        const record = legacyFileEntityDataToFileRecord(
          "image",
          entity.data as unknown as Record<string, unknown>,
        );
        return (
          <FileRecordCard
            platform={entity.platform}
            {...record}
            {...imageRest}
            isSelected={!!selected}
            menuSlot={menuSlot}
            showFooter={showFooter}
            embedded={embedded}
            mediaIntent={embedded ? "full" : "thumbnail"}
          />
        );
      }
      case "file_record": {
        const { selected, menuSlot, showFooter, embedded, ...fileRecordRest } =
          rest;
        return (
          <FileRecordCard
            platform={entity.platform}
            {...(entity.data as unknown as FileRecord)}
            {...fileRecordRest}
            isSelected={!!selected}
            menuSlot={menuSlot}
            showFooter={showFooter}
            embedded={embedded}
            mediaIntent={embedded ? "full" : "thumbnail"}
          />
        );
      }
      case "ticket":
        return (
          <TicketCard platform={entity.platform} {...entity.data} {...rest} />
        );
      case "call":
        return (
          <CallCard platform={entity.platform} {...entity.data} {...rest} />
        );
    }
  }
};

export const getEntityId = (entity: Entity): string => {
  if (entity?.data && "id" in entity.data) {
    return String(entity.data.id);
  }

  return "";
};

export const getEntityType = (entity: Entity): string => {
  if ("type" in entity) {
    return entity.type;
  }

  return entity.base;
};

export const getEntityData = (entity: Entity): any => {
  // All entities use the 'data' field
  return (entity as any).data;
};

export const getEntityName = (entity: Entity): string => {
  // Handle type-based entities (artifacts, skills, etc.)
  if ("type" in entity && (entity as any).type === "artifact") {
    const artifactData = (entity as any).data;
    return artifactData.title || `Untitled ${artifactData.type}`;
  }

  if ("type" in entity && (entity as any).type === "skill") {
    const skillData = (entity as any).data;
    return skillData.name || "Untitled Skill";
  }

  if (
    "type" in entity &&
    (entity as any).type === "canvas_action_item_execution"
  ) {
    const executionData = (entity as any).data;
    return executionData.action_item_name || "Action Item Execution";
  }

  if ("base" in entity) {
    switch (entity.base) {
      case "generic":
        return entity.data.title || "Untitled";
      case "action_item":
        return entity.data.title;
      case "contact":
        return entity.data.name;
      case "email":
        return entity.data.subject;
      case "email_thread":
        return entity.data.subject;
      case "email_folder":
        return entity.data.name;
      case "deal":
        return entity.data.name;
      case "comment":
        return entity.data.text;
      case "company":
        return entity.data.name;
      case "note":
        return "Note";
      case "tool":
        return entity.data.display_name || entity.data.name || "Unnamed Tool";
      case "calendar":
        return entity.data.summary;
      case "calendar_event":
        return entity.data.summary;
      case "database":
        return entity.data.title;
      case "document":
        return entity.data.title;
      case "spreadsheet":
        return entity.data.title;
      case "project":
        return entity.data.name;
      case "repo":
        return entity.data.full_name || entity.data.title;
      case "issue":
        return entity.data.summary;
      case "issue_type":
        return entity.data.name;
      case "label":
        return entity.data.name;
      case "iteration":
        return entity.data.title;
      case "item":
        return entity.data.content_title || "Project Item";
      case "pull_request":
        return `#${entity.data.number}: ${entity.data.title}`;
      case "channel":
        const channelName =
          entity.data.is_im && entity.data.im_user_name
            ? entity.data.im_user_name
            : entity.data.name;
        return channelName;
      case "message":
        return entity.data.content?.substring(0, 50) || "";
      case "file":
        return entity.data.title;
      case "file_folder":
        return entity.data.title;
      case "group":
        return entity.data.name;
      case "image":
        return "title" in entity.data
          ? entity.data.title
          : `Image (${entity.data.id})`;
      case "meeting":
        return entity.data.title;
      case "ticket":
        return entity.data.name;
      case "call":
        return entity.data.title;
      case "video":
        return entity.data.title;
      case "file_record": {
        const d = entity.data as { path?: string; title?: string; id?: string };
        if (d.path) {
          const segs = d.path.split("/").filter(Boolean);
          const leaf = segs[segs.length - 1];
          if (leaf) return leaf;
        }
        return d.title || d.id || "";
      }
      default: {
        if ("title" in entity.data) {
          return entity.data.title || "";
        }

        if ("name" in entity.data) {
          return entity.data.name || "";
        }

        if (entity.base) {
          return capitalCase(entity.base);
        }
      }
    }
  }

  return "";
};

export const EntityAvatar = ({
  entity,
  className,
  style,
}: {
  entity: Entity;
  className?: string;
  style?: CSSProperties;
}) => {
  // Mirror EntityCard: legacy canvas_file uploads no longer render anywhere.
  if (isLegacyCanvasFileEntity(entity)) {
    return null;
  }

  // Handle canvas action item execution by type
  if (
    "type" in entity &&
    (entity as any).type === "canvas_action_item_execution"
  ) {
    const executionData = (entity as any).data;
    // Derive "skipped" status when started is false and skip_reason is present
    const derivedStatus =
      !executionData.started && executionData.skip_reason
        ? "skipped"
        : executionData.status;
    return (
      <ActionItemExecutionCardAvatar
        status={derivedStatus}
        className={className}
        style={style}
      />
    );
  }

  // Legacy artifact entities — adapt to file_record and use the file thumbnail
  // so list/avatar surfaces don't render an empty slot post-Phase 8 cleanup.
  if (isArtifactEntity(entity)) {
    const record = legacyArtifactEntityDataToFileRecord(
      entity.data as unknown as Record<string, unknown>,
    );
    return (
      <FileRecordThumbnail
        contentType={record.content_type}
        previewUrl={record.url}
        alt={record.path}
        className={cn("h-10 w-10 shrink-0 rounded-lg", className)}
        iconClassName="h-5 w-5"
      />
    );
  }

  if ("base" in entity) {
    switch (entity.base) {
      case "generic":
        return (
          <GenericCardAvatar
            platform_name={entity.data.platform_name}
            className={className}
            style={style}
          />
        );
      case "action_item":
        return <ActionItemCardAvatar className={className} style={style} />;
      case "contact":
        return (
          <ContactCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "email":
        return (
          <EmailCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "email_thread":
        return (
          <EmailThreadCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "email_folder":
        return (
          <EmailFolderCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "deal":
        return (
          <DealCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "comment":
        return (
          <CommentCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "company":
        return (
          <CompanyCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "database":
        return (
          <DatabaseCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "note":
        return (
          <NoteCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "tool":
        return (
          <ToolCardAvatar
            platform_name={entity.data.platform_name}
            platform_image_url={entity.data.platform_image_url}
            className={className}
            style={style}
          />
        );
      case "calendar":
        return (
          <CalendarCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "calendar_event":
        return (
          <CalendarEventCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "document":
        return (
          <DocumentCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
            actions={undefined}
          />
        );
      case "spreadsheet":
        return (
          <SpreadsheetCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "project":
        return (
          <ProjectCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "repo":
        return (
          <RepositoryCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "issue":
        return (
          <IssueCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "issue_type":
        return (
          <IssueTypeCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "label":
        return (
          <LabelCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "iteration":
        return (
          <IterationCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "item":
        return (
          <ProjectItemCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "pull_request":
        return (
          <PullRequestCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "channel":
        return (
          <ChannelCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "message":
        return (
          <MessageCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "meeting":
        return (
          <MeetingCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "file":
        return (
          <FileFolderCardAvatar
            icon_link={(entity.data as any).icon_link}
            title={(entity.data as any).title}
            className={className}
            style={style}
          />
        );
      case "video": {
        const record = legacyFileEntityDataToFileRecord(
          "video",
          entity.data as unknown as Record<string, unknown>,
        );
        return (
          <FileRecordThumbnail
            contentType={record.content_type}
            previewUrl={record.url}
            alt={record.path}
            className={cn("h-10 w-10 shrink-0 rounded-lg", className)}
            iconClassName="h-5 w-5"
          />
        );
      }
      case "file_folder":
        return (
          <FileFolderCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "group":
        return (
          <GroupCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "image": {
        const record = legacyFileEntityDataToFileRecord(
          "image",
          entity.data as unknown as Record<string, unknown>,
        );
        return (
          <FileRecordThumbnail
            contentType={record.content_type}
            previewUrl={record.url}
            alt={record.path}
            className={cn("h-10 w-10 shrink-0 rounded-lg", className)}
            iconClassName="h-5 w-5"
          />
        );
      }
      case "ticket":
        return (
          <TicketCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      case "call":
        return (
          <CallCardAvatar
            platform={entity.platform}
            {...entity.data}
            className={className}
            style={style}
          />
        );
      default:
        break;
    }
  }

  return null;
};
